﻿import React, { useState, useEffect, useRef } from 'react';
import PropTypes, { } from 'prop-types';
import { Popover, Button, Menu, MenuItem, Position, Intent } from "@blueprintjs/core";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { ShowHide } from 'components/layout';
import { HelperFunctions } from 'helpers'
import { GridLayout } from "components/grid";
import './Filter.css';
import { initialFilterState } from 'state/reducers/FilteringReducer';

const defaultArray = [];

export function Filter(
    {
        id = null,
        loading = false,
        disabled = false,
        visible = false,
        defaultFilterValues = defaultArray,
        customRenderers = {},
        numOfColumns = 4,
        gap = 20,
        filterName,
        filters,
        onClickOutside,
        onUpdate
    }
) {
    const filterRef = useRef(null);
    const selectedOptions = useSelector(state => state.filters[filterName]) ?? initialFilterState[filterName] ?? defaultArray;
 
    const dispatch = useDispatch();
    const [defaultFilterValuesValue, setDefaultFilterValues] = useState([]);
 
    useEffect(() => {
        if (defaultFilterChanged(defaultFilterValuesValue, defaultFilterValues)){
            setDefaultFilterValues(defaultFilterValues);
            if (defaultFilterValues.length !== 0) {
                dispatch(SetFiltering(filterName, defaultFilterValues));
            }
        }
 
    }, [defaultFilterValues]);
 
    function defaultFilterChanged(arr1, arr2) {
        if (arr1.length !== arr2.length) return true;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i].name !== arr2[i].name ||
                arr1[i].value !== arr2[i].value) return true;
        }
        return false;
    }
 
 
    useEffect(function () {
        onUpdate(selectedOptions);
    }, [selectedOptions]);
 
    useEffect(() => {
        function handleClickOutside(event) {
            const popoverElement = event.target.closest('.bp6-popover');
            const filterButtonElement = event.target.closest('.filter-count-container');
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                !popoverElement &&
                !filterButtonElement
            ) {
                onClickOutside();
            }
        }
        if (visible) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [visible]);
 
    function onItemSelect(filterNameValue, selectedItem, singleSelect = false) {
        var clonedSelected = [
            ...selectedOptions
        ];
 
        if (isItemSelected(filterNameValue, selectedItem.id)) {
            clonedSelected = clonedSelected.filter(function (instance) {
                return instance.name.toLowerCase() !== filterNameValue.toLowerCase()
                    || instance.value.toString().toLowerCase() !== selectedItem.id.toString().toLowerCase();
            });
        } else {
            clonedSelected.push({
                name: filterNameValue,
                displayName: selectedItem.name,
                value: selectedItem.id,
                ...(selectedItem.name === "Uncategorised" && selectedItem.parentAssetTypeId
                    ? { parentAssetTypeId: selectedItem.parentAssetTypeId }
                    : {})
            });
        }
 
        // if single select, remove any other selections for the specific filter that the selected item belongs to
        if (singleSelect) {
            clonedSelected = clonedSelected.filter((instance) => {
                return instance.name.toLowerCase() !== filterNameValue.toLowerCase() || instance.value.toString().toLowerCase() === selectedItem.id.toString().toLowerCase();
            });
        }
 
        dispatch(SetFiltering(filterName, clonedSelected));
    }
 
    // Clears only the selections for a specific filters
    function clearSelectedForFilter(filterNames) {
        const remaining = selectedOptions.filter(opt => !filterNames.includes(opt.name));
        dispatch(SetFiltering(filterName, remaining));
    }
 
    function clearFilters() {
        dispatch(SetFiltering(filterName, []));
    }
 
    function setFilterText(filterNameValue) {
 
        var filterSelectedOptions = selectedOptions.filter(function (instance) {
            return instance.name.toLowerCase() === filterNameValue.toLowerCase() && (instance.value.toString().length > 0);
        });
 
        switch (filterSelectedOptions.length) {
            case 0:
                return 'Select filter';
            case 1:
                return filterSelectedOptions[0].displayName;
            default:
                return filterSelectedOptions[0].displayName + ', (+' + (filterSelectedOptions.length - 1) + ' more)';
        }
    }
 
    function isItemSelected(filterNameValue, selectedItemValue) {
        return selectedOptions.filter(function (instance) {
            return (selectedItemValue == null || instance.value == null ? false : instance.name.toLowerCase() === filterNameValue.toLowerCase() && instance.value.toString().toLowerCase() === selectedItemValue.toString().toLowerCase());
        }).length > 0;
    }
 
    function generateDisplayIcon(name, idValue) {
 
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        }
 
        return isItemSelected(name, idValue) ? "tick" : "";
    }
 
    // Calculate blank columns to fill the grid, ensuring space for the "CLEAR ALL" button
    const filterCount = filters?.length || 0;
    const blankColumns = numOfColumns - (filterCount % numOfColumns) - 1;
 
    function renderBlankColumns() {
        if (blankColumns > 0) {
            return [...Array(blankColumns)].map((_) => (
                <div key="blank-column" />
            ));
        }
        return null;
    }
 
    return (
        <ShowHide
            evaluator={visible}
            show={(
                <div className="filter-container" ref={filterRef}>                    
                    <GridLayout numberOfColumns={numOfColumns} gap={gap} className="filter-grid-auto-width">
                        {
                            filters != null && filters.map(function (filter) {
                                if (customRenderers && typeof customRenderers[filter.name] === 'function') {
                                    return customRenderers[filter.name]({
                                        filter,
                                        selectedOptions,
                                        onItemSelect,
                                        isItemSelected,
                                        generateDisplayIcon,
                                        clearSelectedForFilter
                                    });
                                }
                                return (
                                    <div key={filter.name}>
                                        <div className="filter-header">{filter.displayName != null ? filter.displayName : filter.name}</div>
                                        <div className="filter-popover-container">
                                            <div className="dropdown-renderer">
                                                <Popover
                                                    key={filter.name}
                                                    minimal
                                                    popoverClassName="dropdown-list-behaviour"
                                                    position={Position.BOTTOM_LEFT}
                                                    content={
                                                        <Menu id={`filter-${HelperFunctions.kebabCase(filter.name)}-menu`}>
                                                            {filter.items.map(item => {
                                                                return (
                                                                    <MenuItem
                                                                        key={item.name}
                                                                        text={item.name}
                                                                        onClick={e => {
                                                                            //Don't close the popover on click
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            onItemSelect(filter.name, item, filter.singleSelect);
                                                                        }}
                                                                        icon={generateDisplayIcon(filter.name, item.id)}
                                                                    />
                                                                )
                                                            })}
                                                        </Menu>
                                                    }
                                                >
                                                    <div className="filter-button-bordered">
                                                        <Button
                                                            className="filter-button"
                                                            text={setFilterText(filter.name)}
                                                            disabled={disabled}
                                                            endIcon="chevron-down"
                                                            variant="minimal"
                                                            id={`filter-${HelperFunctions.kebabCase(filter.name)}-btn`}
                                                        />
                                                    </div>
                                                </Popover>
                                            </div>
                                            <Button
                                                icon="cross"
                                                variant="minimal"
                                                intent={Intent.NONE}
                                                onClick={() => clearSelectedForFilter([filter.name])}
                                                disabled={disabled}
                                                aria-label={`Clear ${filter.displayName} filter`}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        }
                        {renderBlankColumns()}
                        <ShowHide
                            evaluator={selectedOptions.length > 0}
                            show={(
                                <div className="filter-reset-button">
                                    <Button
                                        text="CLEAR ALL"
                                        size="large"
                                        intent={Intent.DANGER}
                                        variant="minimal"
                                        onClick={clearFilters}
                                        icon="cross"
                                        disabled={disabled}
                                    />
                                </div>
                            )}
                        />
                    </GridLayout>
                </div>
            )}
        />
    );
}

Filter.propTypes = {
    filters: PropTypes.array,
    filterName: PropTypes.string.isRequired,
    id: PropTypes.string,
    loading: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    visible: PropTypes.bool,
    defaultFilterValues: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            singleSelect: PropTypes.bool,
            displayName: PropTypes.string.isRequired,
        })
    ),
    customRenderers: PropTypes.object,
    numOfColumns: PropTypes.number,
    gap: PropTypes.number,
    onClickOutside: PropTypes.func.isRequired
};